import { makeStyles } from "@material-ui/core/styles";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { DropOffLocations_dropOffLocations_edges_node } from "@saleor/dropOff/types/DropOffLocations";
import { maybe } from "@saleor/misc";
import { OrderCourierEnum } from "@saleor/motherOrders/utils/enums";
import { RemittanceRunnerTblColDataText } from "@saleor/remittanceRunner/components/RemittanceRunnerTableColumns";
import { remittanceRunnerPath } from "@saleor/remittanceRunner/url";
import {
  DeliveryTypeEnum,
  OrderVerticalType,
  SmopOrderStatusEnum
} from "@saleor/types/globalTypes";
import { canManageRemittance } from "@saleor/utils/handlers/userHandler";
import moment from "moment-timezone";
import React from "react";
import { FormattedMessage } from "react-intl";

import { OrderDetails_order } from "../../types/OrderDetails";
import OrderDeliveryType from "../OrderDeliveryType";

const useStyles = makeStyles(
  theme => ({
    cancelButton: {
      "& span": {
        color: "#E26139"
      },
      border: "1px solid #E26139"
    },
    remittanceTableData: {
      alignItems: "center",
      display: "flex",
      fontSize: "14px",
      justifyContent: "flex-end"
    },
    root: {
      ...theme.typography.body1,
      lineHeight: 1.9,
      width: "100%"
    },
    textBold: {
      fontSize: "1rem",
      fontWeight: "bold",
      whiteSpace: "nowrap"
    },
    textRight: {
      fontSize: "14px",
      textAlign: "right",
      whiteSpace: "nowrap"
    }
  }),
  { name: "OrderInfo" }
);

export interface OrderInfoProps {
  canUpdateHomeLocation: boolean;
  order: OrderDetails_order;
  dropOffList: DropOffLocations_dropOffLocations_edges_node[];
  onAssignHomeLocation: (id: string) => void;
  onCancel();
}

const OrderInfo: React.FC<OrderInfoProps> = props => {
  const {
    canUpdateHomeLocation,
    order,
    dropOffList,
    onAssignHomeLocation
  } = props;
  const classes = useStyles(props);

  const checkVerticalType = (verticalType: OrderVerticalType) => {
    if (verticalType === OrderVerticalType.GENERAL_MERCHANDISE) {
      return "General Merchandise";
    } else if (verticalType === OrderVerticalType.FOOD_AND_BEVERAGES) {
      return "Food and Beverages";
    } else if (verticalType === OrderVerticalType.GROCERY) {
      return "Grocery";
    } else {
      return "";
    }
  };

  const userCanManageRemittance = canManageRemittance();

  const isValidOrder =
    order?.payments[0]?.paymentMethodType === "cash" &&
    !(
      order?.courier === OrderCourierEnum.PANDAGO ||
      order?.courier === OrderCourierEnum.PANDAGO_AUTO
    );

  const dropOffListChoices = dropOffList?.map(e => ({
    label: e.name,
    value: e.id
  }));

  const binMessage =
    order?.binCode && !order?.bin ? "Assigned Bin" : "Dispatch Bin";

  return (
    <>
      <table className={classes.root}>
        <tbody>
          <tr>
            <td className={classes.textBold}>
              <FormattedMessage
                defaultMessage="Delivery Type"
                description="order total"
              />
            </td>
            <td className={classes.textRight}>
              {maybe(() => order.shippingMethod.deliveryType) ? (
                <OrderDeliveryType
                  delType={order.shippingMethod.deliveryType}
                />
              ) : (
                " "
              )}
            </td>
          </tr>
          <tr>
            <td className={classes.textBold}>
              <FormattedMessage
                defaultMessage="Classification"
                description="order total"
              />
            </td>
            <td className={classes.textRight}>
              {maybe(() => order.verticalType) ? (
                <span>
                  {checkVerticalType(maybe(() => order.verticalType))}
                </span>
              ) : (
                " "
              )}
            </td>
          </tr>
          <tr>
            <td className={classes.textBold}>
              <FormattedMessage
                defaultMessage="Order Type"
                description="order detail type"
              />
            </td>
            <td className={classes.textRight}>
              {order && order.isMultiTenant ? "Multi-tenant" : "Single-tenant"}
            </td>
          </tr>
          <tr>
            <td className={classes.textBold}>
              <FormattedMessage
                defaultMessage="Order Date"
                description="order detail date"
              />
            </td>
            <td className={classes.textRight}>
              {order && moment(order.created).format("lll")}
            </td>
          </tr>
          <tr>
            <td className={classes.textBold}>
              <FormattedMessage
                defaultMessage="Delivery Schedule"
                description="order detail delivery schedule"
              />
            </td>
            <td className={classes.textRight}>
              {order && order.shippingSchedule
                ? moment(order.shippingSchedule).format("ll")
                : "-"}
            </td>
          </tr>
          {/* Home Loc */}
          <tr>
            <td className={classes.textBold}>
              <FormattedMessage
                defaultMessage="Home Location"
                description="home location text"
              />
            </td>
            {order?.shippingMethod.deliveryType === DeliveryTypeEnum.NEXT_DAY ||
            order?.shippingMethod.deliveryType ===
              DeliveryTypeEnum.IN_STORE_PICKUP ? (
              <td
                className={classes.textRight}
                style={{ textAlign: "left", width: "25%" }}
              >
                <SingleSelectField
                  label="Home Location"
                  choices={dropOffListChoices}
                  onChange={e => onAssignHomeLocation(e.target.value)}
                  value={order?.dropOffLocation?.id}
                  disabled={
                    [
                      SmopOrderStatusEnum.SHIPPED,
                      SmopOrderStatusEnum.COMPLETED,
                      SmopOrderStatusEnum.CANCELED
                    ].includes(order?.smopStatus) || !canUpdateHomeLocation
                  }
                />
              </td>
            ) : (
              <td className={classes.textRight}>
                {order?.dropOffLocation?.name}
              </td>
            )}
          </tr>
          {isValidOrder && (
            <tr>
              <td className={classes.textBold}>
                <FormattedMessage
                  defaultMessage="Remittance ID"
                  description="Label for Remittance ID"
                />
              </td>
              <td className={classes.remittanceTableData}>
                <RemittanceRunnerTblColDataText
                  dataTitle={"Remittance ID"}
                  dataValue={order?.remittanceLine?.remittance?.remittanceNo}
                  isClickable={userCanManageRemittance}
                  isOrderInfo={true}
                  onClick={() => {
                    window.open(
                      remittanceRunnerPath(
                        order?.remittanceLine?.remittance?.id
                      )
                    );
                  }}
                />
              </td>
            </tr>
          )}
          {order?.shippingMethod?.deliveryType === DeliveryTypeEnum.EXPRESS ||
          order?.shippingMethod?.deliveryType ===
            DeliveryTypeEnum.EXPRESS_LONG_DISTANCE ||
          order?.shippingMethod?.deliveryType === DeliveryTypeEnum.SAME_DAY ? (
            <tr>
              <td className={classes.textBold}>{binMessage}</td>
              <td className={classes.textRight}>
                {order && order?.binCode ? order?.binCode : "-"}
              </td>
            </tr>
          ) : (
            ""
          )}
        </tbody>
      </table>
    </>
  );
};
OrderInfo.displayName = "OrderInfo";
export default OrderInfo;
